import { useState, useEffect, useMemo } from "react";
import { useMcpSchema, type ColumnInfo } from "@/hooks/useMcpSchema";
import {
  extractWhereParams,
  extractFromTables,
  detectFilterType,
  replaceWhereValuesWithPlaceholders,
  type ExtractedWhereParam,
} from "@/lib/auto-filters";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2,
  Search,
  CalendarIcon,
  Hash,
  Type,
  List,
  ListChecks,
  Table2,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FilterControlType =
  | "date"
  | "number"
  | "text"
  | "singleselect"
  | "multiselect";

export interface ConfiguredFilter {
  id: string;
  fieldName: string;
  tableName: string;
  dataType: string;
  filterType: FilterControlType;
  label: string;
  queryId: string;
  queryName: string;
}

/** Result returned from the dialog: filters + modified widget SQL queries. */
export interface FilterSuggestionsResult {
  filters: ConfiguredFilter[];
  /** Updated widgets with placeholder-replaced SQL. Index matches the input widgets array. */
  updatedWidgets: Array<{ sqlQuery: string; _queryName?: string }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function filterTypeIcon(type: FilterControlType) {
  switch (type) {
    case "date":
      return CalendarIcon;
    case "number":
      return Hash;
    case "text":
      return Type;
    case "singleselect":
      return List;
    case "multiselect":
      return ListChecks;
  }
}

function filterTypeLabel(type: FilterControlType) {
  switch (type) {
    case "date":
      return "Date / Time";
    case "number":
      return "Number";
    case "text":
      return "Text";
    case "singleselect":
      return "Single Select";
    case "multiselect":
      return "Multi Select";
  }
}

function toLabel(name: string): string {
  return name
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Columns that are structural (joins, IDs) rather than user-facing filters. */
const SKIP_COLUMN_PATTERNS = [
  /^id$/i,
  /^uuid$/i,
  /^_id$/i,
  /^pk$/i,
  /^fk_/i,
  /^created_by$/i,
  /^updated_by$/i,
  /^deleted_at$/i,
  /^deleted_by$/i,
];

// ─── Component ────────────────────────────────────────────────────────────────

interface FilterSuggestionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Widget specs from the dashboard that contain inline SQL queries */
  widgets: Array<{ sqlQuery?: string; _queryName?: string }>;
  /** Already configured filters (to pre-check) */
  existingFilters: ConfiguredFilter[];
  /** Called when user confirms selection. Returns filters + modified widgets. */
  onSave: (result: FilterSuggestionsResult) => void;
}

interface WhereCandidate {
  fieldName: string;
  tableName: string;
  dataType: string;
  suggestedType: FilterControlType;
  /** The hardcoded value found in the WHERE clause */
  defaultValue: string;
  operator: string;
  /** Index of the widget this came from */
  widgetIndex: number;
  queryName: string;
}

const FilterSuggestionsDialog = ({
  open,
  onOpenChange,
  widgets,
  existingFilters,
  onSave,
}: FilterSuggestionsDialogProps) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<
    Map<string, { type: FilterControlType; label: string; defaultValue: string }>
  >(new Map());

  // Fetch MCP schema for column type info
  const { schemaData, isLoading: schemaLoading } = useMcpSchema();

  // Build WHERE-based field candidates by analyzing widget SQL queries
  const whereCandidates = useMemo(() => {
    if (schemaData.length === 0) return [];

    const candidates: WhereCandidate[] = [];
    const seen = new Set<string>();

    // Build column lookup: table.column → ColumnInfo
    const columnLookup = new Map<string, ColumnInfo & { tableName: string }>();
    const columnToTables = new Map<string, { tableName: string; col: ColumnInfo }[]>();

    for (const t of schemaData) {
      for (const c of t.columns) {
        columnLookup.set(
          `${t.table_name.toLowerCase()}.${c.column_name.toLowerCase()}`,
          { ...c, tableName: t.table_name }
        );
        const key = c.column_name.toLowerCase();
        if (!columnToTables.has(key)) columnToTables.set(key, []);
        columnToTables.get(key)!.push({ tableName: t.table_name, col: c });
      }
    }

    for (let wi = 0; wi < widgets.length; wi++) {
      const widget = widgets[wi];
      if (!widget.sqlQuery?.trim()) continue;

      const sql = widget.sqlQuery;
      const fromTables = extractFromTables(sql);
      const whereParams = extractWhereParams(sql);
      const queryName = widget._queryName || `Widget ${wi + 1}`;

      for (const param of whereParams) {
        // Skip structural columns
        if (SKIP_COLUMN_PATTERNS.some((p) => p.test(param.columnName))) continue;

        // Skip if value looks like an existing placeholder
        if (/\{\{.*\}\}/.test(param.rawValue)) continue;

        // Resolve table name and column type from schema
        let resolvedTable: string | null = null;
        let colInfo: ColumnInfo | null = null;

        if (param.tableName) {
          const realTable = fromTables.get(param.tableName.toLowerCase()) || param.tableName;
          const lookup = columnLookup.get(`${realTable.toLowerCase()}.${param.columnName.toLowerCase()}`);
          if (lookup) {
            resolvedTable = lookup.tableName;
            colInfo = lookup;
          }
        } else {
          // Try FROM tables first
          for (const [, realTable] of fromTables) {
            const lookup = columnLookup.get(`${realTable.toLowerCase()}.${param.columnName.toLowerCase()}`);
            if (lookup) {
              resolvedTable = lookup.tableName;
              colInfo = lookup;
              break;
            }
          }
          // Fallback: global lookup if unique
          if (!resolvedTable) {
            const cs = columnToTables.get(param.columnName.toLowerCase());
            if (cs && cs.length === 1) {
              resolvedTable = cs[0].tableName;
              colInfo = cs[0].col;
            }
          }
        }

        if (!resolvedTable || !colInfo) continue;

        const dedupKey = `${resolvedTable.toLowerCase()}.${colInfo.column_name.toLowerCase()}`;
        if (seen.has(dedupKey)) continue;
        seen.add(dedupKey);

        const suggestedType = detectFilterType(colInfo.data_type, colInfo.column_name);

        candidates.push({
          fieldName: colInfo.column_name,
          tableName: resolvedTable,
          dataType: colInfo.data_type,
          suggestedType,
          defaultValue: param.value,
          operator: param.operator,
          widgetIndex: wi,
          queryName,
        });
      }
    }

    return candidates;
  }, [schemaData, widgets]);

  // Initialize selected from existingFilters
  useEffect(() => {
    if (!open) return;
    const map = new Map<string, { type: FilterControlType; label: string; defaultValue: string }>();
    existingFilters.forEach((f) => {
      const key = `${f.tableName.toLowerCase()}.${f.fieldName.toLowerCase()}`;
      map.set(key, { type: f.filterType, label: f.label, defaultValue: "" });
    });
    setSelected(map);
    setSearch("");
  }, [open, existingFilters]);

  const filteredCandidates = useMemo(() => {
    if (!search.trim()) return whereCandidates;
    const q = search.toLowerCase();
    return whereCandidates.filter(
      (c) =>
        c.fieldName.toLowerCase().includes(q) ||
        c.tableName.toLowerCase().includes(q) ||
        c.dataType.toLowerCase().includes(q) ||
        c.queryName.toLowerCase().includes(q)
    );
  }, [whereCandidates, search]);

  // Group by widget/query
  const groupedCandidates = useMemo(() => {
    const groups = new Map<number, { queryName: string; fields: WhereCandidate[] }>();
    filteredCandidates.forEach((c) => {
      if (!groups.has(c.widgetIndex)) {
        groups.set(c.widgetIndex, { queryName: c.queryName, fields: [] });
      }
      groups.get(c.widgetIndex)!.fields.push(c);
    });
    return groups;
  }, [filteredCandidates]);

  const toggleField = (candidate: WhereCandidate) => {
    const key = `${candidate.tableName.toLowerCase()}.${candidate.fieldName.toLowerCase()}`;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          type: candidate.suggestedType,
          label: toLabel(candidate.fieldName),
          defaultValue: candidate.defaultValue,
        });
      }
      return next;
    });
  };

  const updateType = (key: string, type: FilterControlType) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const existing = next.get(key);
      if (existing) {
        next.set(key, { ...existing, type });
      }
      return next;
    });
  };

  const handleSave = () => {
    // Build filters list
    const filters: ConfiguredFilter[] = [];
    const replacements: Array<{
      columnName: string;
      tableName: string | null;
      filterType: FilterControlType;
      label: string;
      defaultValue: string;
    }> = [];

    selected.forEach((config, key) => {
      const dotIdx = key.indexOf(".");
      const tableName = key.substring(0, dotIdx);
      const fieldName = key.substring(dotIdx + 1);

      // Find the matching candidate to get data type
      const candidate = whereCandidates.find(
        (c) =>
          c.tableName.toLowerCase() === tableName &&
          c.fieldName.toLowerCase() === fieldName
      );

      const filterId = `auto:${tableName}.${fieldName}`;

      filters.push({
        id: filterId,
        fieldName: candidate?.fieldName || fieldName,
        tableName: candidate?.tableName || tableName,
        dataType: candidate?.dataType || "text",
        filterType: config.type,
        label: config.label,
        queryId: "",
        queryName: candidate?.queryName || "",
      });

      replacements.push({
        columnName: candidate?.fieldName || fieldName,
        tableName: candidate?.tableName || null,
        filterType: config.type,
        label: config.label,
        defaultValue: config.defaultValue || candidate?.defaultValue || "",
      });
    });

    // Replace hardcoded values with placeholders in all widget queries
    const updatedWidgets = widgets.map((w) => {
      if (!w.sqlQuery?.trim()) return { sqlQuery: w.sqlQuery || "", _queryName: w._queryName };

      const modifiedSql = replaceWhereValuesWithPlaceholders(w.sqlQuery, replacements);
      return { sqlQuery: modifiedSql, _queryName: w._queryName };
    });

    onSave({ filters, updatedWidgets });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Suggest Filters</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Parameters detected from WHERE conditions in your widget queries.
            Select the ones you want to add as dashboard filters.
            Selected parameters will be replaced with placeholder variables in
            all queries.
          </p>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search fields, tables, or queries..."
            className="pl-9 h-9"
          />
        </div>

        <ScrollArea className="flex-1 min-h-0 max-h-[50vh]">
          {schemaLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : whereCandidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
              <Table2 className="h-8 w-8" />
              <p className="text-sm">
                {widgets.filter((w) => w.sqlQuery?.trim()).length === 0
                  ? "No widgets with SQL queries found. Add widgets with data sources first."
                  : "No WHERE clause parameters detected in the widget queries."}
              </p>
            </div>
          ) : (
            <div className="space-y-4 pr-4">
              {Array.from(groupedCandidates.entries()).map(([widgetIdx, group]) => (
                <div key={widgetIdx}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs">
                      Query
                    </Badge>
                    <span className="text-sm font-medium">{group.queryName}</span>
                    <span className="text-xs text-muted-foreground">
                      {group.fields.length} parameter{group.fields.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-1 ml-2">
                    {group.fields.map((candidate) => {
                      const key = `${candidate.tableName.toLowerCase()}.${candidate.fieldName.toLowerCase()}`;
                      const isSelected = selected.has(key);
                      const config = selected.get(key);
                      const Icon = filterTypeIcon(candidate.suggestedType);

                      return (
                        <div
                          key={key}
                          className={cn(
                            "flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors",
                            isSelected
                              ? "border-primary/50 bg-primary/5"
                              : "border-border hover:bg-accent"
                          )}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleField(candidate)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-mono font-medium truncate">
                                {candidate.fieldName}
                              </span>
                              <Badge variant="secondary" className="font-mono text-[10px] shrink-0">
                                {candidate.dataType}
                              </Badge>
                              {candidate.defaultValue && (
                                <Badge variant="outline" className="text-[10px] shrink-0 max-w-[120px] truncate">
                                  {candidate.operator} {candidate.defaultValue}
                                </Badge>
                              )}
                            </div>
                            <span className="text-[10px] text-muted-foreground">
                              {candidate.tableName}
                            </span>
                          </div>
                          {isSelected && config ? (
                            <Select
                              value={config.type}
                              onValueChange={(v) => updateType(key, v as FilterControlType)}
                            >
                              <SelectTrigger className="h-7 w-36 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="date">Date / Time</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="text">Text</SelectItem>
                                <SelectItem value="singleselect">Single Select</SelectItem>
                                <SelectItem value="multiselect">Multi Select</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                              <Icon className="h-3.5 w-3.5" />
                              <span>{filterTypeLabel(candidate.suggestedType)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {selected.size} filter{selected.size !== 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={selected.size === 0}>
              Apply Filters
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FilterSuggestionsDialog;
